import { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useFilters } from "@/context/FiltersContext";
import { toast } from "sonner";
import { Home, Loader2, Check, ChevronDown } from "lucide-react";
import FanOnboarding from "@/components/FanOnboarding";

export const RODEO_EVENTS = [
  "Bareback", "Saddle Bronc", "Bull Riding", "Tie-Down Roping",
  "Steer Wrestling", "Team Roping (Header)", "Team Roping (Heeler)", "Ladies Barrel Racing", "Ladies Breakaway Roping",
];

// The official PRCA circuits. Shown (optional) once a rider follows PRCA.
export const PRCA_CIRCUITS = [
  "Badlands", "California", "Columbia River", "First Frontier", "Great Lakes",
  "Maple Leaf", "Montana", "Mountain States", "Prairie", "Southeastern", "Texas", "Turquoise", "Wilderness",
];

// Sanctioning bodies a rider can follow. Matches the association list in the Filters sheet.
export const RODEO_ASSOCIATIONS = [
  { v: "PRCA", l: "PRCA", d: "Pro Rodeo (USA)" },
  { v: "CPRA", l: "CPRA", d: "Canadian Pro Rodeo" },
  { v: "CCA", l: "CCA", d: "Canadian Cowboys" },
  { v: "FCA", l: "FCA", d: "Foothills" },
  { v: "LRA", l: "LRA", d: "Lakeland" },
  { v: "BCRA", l: "BCRA", d: "BC Rodeo" },
];

export default function Onboarding() {
  const { user } = useAuth();
  // Fans get a spectator-first setup (favorite riders + events to watch); contestants
  // keep the full athlete setup below. Branch at the top so no hooks run conditionally.
  if (user?.account_type === "fan") return <FanOnboarding />;
  return <ContestantOnboarding />;
}

function ContestantOnboarding() {
  const { user } = useAuth();
  const { applyProfilePrefs } = useFilters();
  const [show, setShow] = useState(false);
  const [town, setTown] = useState("");
  const [picked, setPicked] = useState(new Set());
  const [assocPicked, setAssocPicked] = useState(new Set());
  const [circuit, setCircuit] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (user?.role === "reviewer" || user?.role === "admin") return; // staff aren't athletes
    api.get("/me/profile").then((r) => {
      if (!r.data.onboarded) setShow(true);
    }).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const toggle = (e) => setPicked((p) => { const n = new Set(p); n.has(e) ? n.delete(e) : n.add(e); return n; });
  const toggleAssoc = (a) => setAssocPicked((p) => {
    const n = new Set(p);
    n.has(a) ? n.delete(a) : n.add(a);
    if (a === "PRCA" && !n.has("PRCA")) setCircuit(""); // drop circuit if PRCA unfollowed
    return n;
  });

  const finish = async () => {
    setSaving(true);
    try {
      if (town.trim().length >= 2) { try { await api.post("/me/home", { town: town.trim() }); } catch { /* keep going */ } }
      // Save the athlete's events + associations to their PROFILE, AND apply them as the
      // active list filters so their picks "set the scene" across Table/Calendar/Map.
      await api.post("/me/events", { events: [...picked] });
      if (assocPicked.size) { try { await api.post("/me/associations", { associations: [...assocPicked] }); } catch { /* keep going */ } }
      if (assocPicked.has("PRCA") && circuit) { try { await api.post("/me/circuit", { circuit, season: new Date().getFullYear() }); } catch { /* keep going */ } }
      applyProfilePrefs({ associations: [...assocPicked], events: [...picked] });
      if (user?.id) { try { localStorage.setItem(`rp_prefs_synced_${user.id}`, "1"); } catch { /* ignore */ } }
      toast.success("You're all set!");
      setShow(false);
    } catch { toast.error("Couldn't save your profile — try again"); }
    finally { setSaving(false); }
  };

  if (!show) return null;
  return (
    <div className="fixed inset-0 z-[3000] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 overflow-y-auto" data-testid="onboarding-modal">
      <div className="bg-white rounded-2xl w-full max-w-lg shadow-2xl overflow-hidden flex flex-col max-h-[90dvh]">
        <div className="bg-[#0C1017] text-white px-6 py-5 shrink-0">
          <div className="text-[11px] tracking-[3px] font-bold text-[#F26D77]">WELCOME TO RODEO PILOT</div>
          <div className="font-display text-2xl font-extrabold mt-1">Let's set up your profile</div>
          <div className="text-sm text-slate-300 mt-1">We'll tailor draws, standings and the map to you.</div>
        </div>
        <div className="p-6 space-y-6 overflow-y-auto">
          <div>
            <label className="text-sm font-bold text-[#14181F] flex items-center gap-2 mb-2"><Home className="w-4 h-4 text-[#C8000F]" /> Your home town</label>
            <input value={town} onChange={(e) => setTown(e.target.value)} placeholder="e.g. Ponoka, AB" data-testid="onboarding-town"
              className="w-full rounded-lg border border-[#D7DBE0] px-3 py-2.5 text-sm focus:outline-none focus:border-[#C8000F]" />
          </div>
          <div>
            <div className="text-sm font-bold text-[#14181F] mb-2">Which events do you compete in?</div>
            <div className="grid grid-cols-2 gap-2">
              {RODEO_EVENTS.map((e) => {
                const on = picked.has(e);
                return (
                  <button key={e} onClick={() => toggle(e)} data-testid={`onboarding-event-${e.replace(/\s/g, "-")}`}
                    className={`flex items-center justify-between gap-2 text-sm font-semibold px-3 py-2.5 rounded-lg border text-left ${on ? "bg-[#FBF4F5] border-[#C8000F] text-[#9E0008]" : "bg-white border-[#D7DBE0] text-slate-600 hover:border-slate-400"}`}>
                    {e} {on && <Check className="w-4 h-4 shrink-0" />}
                  </button>
                );
              })}
            </div>
            <p className="text-xs text-slate-400 mt-2">We'll highlight your events on each draw. Your main list still shows every rodeo — filter anytime.</p>
          </div>
          <div>
            <div className="text-sm font-bold text-[#14181F] mb-2">Which associations do you follow? <span className="font-normal text-slate-400">(optional)</span></div>
            <div className="grid grid-cols-2 gap-2">
              {RODEO_ASSOCIATIONS.map((a) => {
                const on = assocPicked.has(a.v);
                return (
                  <button key={a.v} onClick={() => toggleAssoc(a.v)} data-testid={`onboarding-assoc-${a.v}`}
                    className={`flex items-center justify-between gap-2 text-sm font-semibold px-3 py-2.5 rounded-lg border text-left ${on ? "bg-[#FBF4F5] border-[#C8000F] text-[#9E0008]" : "bg-white border-[#D7DBE0] text-slate-600 hover:border-slate-400"}`}>
                    <span><span className="font-bold">{a.l}</span><span className="block text-[11px] font-normal opacity-70">{a.d}</span></span>
                    {on && <Check className="w-4 h-4 shrink-0" />}
                  </button>
                );
              })}
            </div>
            <p className="text-xs text-slate-400 mt-2">Saved to your profile for standings and draws. We won't hide rodeos — your list still shows every association, filter anytime.</p>
            {assocPicked.has("PRCA") && (
              <div className="mt-4 rounded-lg border border-[#E4C9CB] bg-[#FBF4F5] p-3" data-testid="onboarding-circuit-wrap">
                <div className="text-sm font-bold text-[#14181F] mb-1">Your PRCA circuit <span className="font-normal text-slate-400">(optional)</span></div>
                <div className="relative">
                  <select value={circuit} onChange={(e) => setCircuit(e.target.value)} data-testid="onboarding-circuit"
                    className="w-full appearance-none rounded-lg border border-[#D7DBE0] bg-white px-3 py-2.5 pr-9 text-sm text-slate-700 focus:outline-none focus:border-[#C8000F]">
                    <option value="">Choose your circuit…</option>
                    {PRCA_CIRCUITS.map((c) => <option key={c} value={c}>{c}</option>)}
                  </select>
                  <ChevronDown className="w-4 h-4 text-slate-400 absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none" />
                </div>
                <p className="text-xs text-slate-400 mt-1.5">PRCA members know theirs — we'll use it for circuit standings. Leave blank if you'd rather not.</p>
              </div>
            )}
          </div>
        </div>
        <div className="flex items-center justify-between px-6 py-4 border-t border-slate-100 shrink-0 bg-white">
          <button onClick={finish} className="text-xs text-slate-400 hover:underline" data-testid="onboarding-skip">Skip for now</button>
          <button onClick={finish} disabled={saving} data-testid="onboarding-finish"
            className="flex items-center gap-2 bg-[#C8000F] hover:bg-[#9E0008] text-white font-bold px-6 py-2.5 rounded-lg disabled:opacity-60">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />} Start planning
          </button>
        </div>
      </div>
    </div>
  );
}
